import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Brain, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect } from 'react';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Χαρακτηριστικά', href: '/#features' },
    { name: 'Προγράμματα', href: '/#programs' },
    { name: 'Πακέτα', href: '/pricing' },
    { name: 'Επικοινωνία', href: '/contact' },
  ];

  const isActive = (href) => {
    if (href.startsWith('/#')) return location.pathname === '/';
    return location.pathname === href;
  };

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const elementId = href.replace('/#', '');
      
      if (location.pathname === '/') {
        // Already on home page, just scroll
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        // Navigate to home page first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } else if (href === '/pricing') {
      e.preventDefault();
      navigate('/pricing');
      window.scrollTo(0, 0);
    }
    setMobileMenuOpen(false);
  };

  // Scroll to top on route change
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" onClick={() => window.scrollTo(0, 0)}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center group-hover:scale-105 transition-transform">
              <Brain className="w-5 h-5 text-cyan-400" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              ESPA Manager
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`text-sm font-medium transition-colors hover:text-cyan-600 cursor-pointer ${
                  isActive(link.href) ? 'text-cyan-600' : 'text-slate-600'
                }`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button className="rounded-full px-6 py-5 bg-slate-900 hover:bg-slate-800 text-white font-medium hover:scale-105 transition-all">
                Είσοδος
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-sm font-medium text-slate-600 hover:text-cyan-600 transition-colors cursor-pointer"
                >
                  {link.name}
                </a>
              ))}
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium">
                  Είσοδος
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                ESPA Manager
              </span>
            </div>
            <p className="text-slate-400 max-w-sm leading-relaxed">
              Η πιο έξυπνη πλατφόρμα διαχείρισης αιτήσεων χρηματοδότησης. 
              ΕΣΠΑ, ΟΑΕΔ, Αναπτυξιακός Νόμος - όλα σε ένα μέρος.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Προϊόν</h4>
            <ul className="space-y-3">
              <li><Link to="/#features" className="text-slate-400 hover:text-cyan-400 transition-colors">Χαρακτηριστικά</Link></li>
              <li><Link to="/pricing" className="text-slate-400 hover:text-cyan-400 transition-colors">Πακέτα & Τιμές</Link></li>
              <li><Link to="/#programs" className="text-slate-400 hover:text-cyan-400 transition-colors">Προγράμματα</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Επικοινωνία</h4>
            <ul className="space-y-3">
              <li><Link to="/contact" className="text-slate-400 hover:text-cyan-400 transition-colors">Φόρμα Επικοινωνίας</Link></li>
              <li><a href="mailto:espamanagerapp@gmail.com" className="text-slate-400 hover:text-cyan-400 transition-colors">espamanagerapp@gmail.com</a></li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} ESPA Manager. Με επιφύλαξη κάθε δικαιώματος.
          </p>
        </div>
      </div>
    </footer>
  );
};

const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />
      <main className="flex-1 pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default PublicLayout;
